import React, { useState, useEffect } from "react";
import { Paper } from "@mui/material";
import { useDarkMode } from "../../../context/darkModeContext.jsx";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../../../utils/axios-instance.js";
import MailBoxWidget from "./MailBoxWidget";
import MailFullClient from "./MailFullClient";
import { gapi } from "gapi-script";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const DISCOVERY_DOCS = [
  "https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest",
];
const SCOPES = [
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.send",
];

const MailWidget = () => {
  const { darkMode } = useDarkMode();

  // State management
  const [activeProvider, setActiveProvider] = useState("gmail");
  const [gmailConnected, setGmailConnected] = useState(false);
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [emails, setEmails] = useState([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [gmailEmail, setGmailEmail] = useState("");
  const [gmailProfilePic, setGmailProfilePic] = useState("");
  const [googleToken, setGoogleToken] = useState(() =>
    localStorage.getItem("google_token")
  );

  useEffect(() => {
    const initializeGapi = async () => {
      try {
        let authInstance = gapi.auth2 && gapi.auth2.getAuthInstance();
        if (!authInstance) {
          await gapi.client.init({
            apiKey: API_KEY,
            clientId: CLIENT_ID,
            discoveryDocs: DISCOVERY_DOCS,
            scope: SCOPES.join(" "),
          });
          authInstance = gapi.auth2.getAuthInstance();
        }

        if (authInstance.isSignedIn.get()) {
          const currentUser = authInstance.currentUser.get();
          const hasScopes = SCOPES.every((scope) =>
            currentUser.hasGrantedScopes(scope)
          );

          if (hasScopes) {
            const token = currentUser.getAuthResponse(true)?.access_token;
            const email = currentUser.getBasicProfile()?.getEmail() || "";
            const imageUrl = currentUser.getBasicProfile()?.getImageUrl() || "";

            if (token) {
              setGoogleToken(token);
              setGmailEmail(email);
              setGmailProfilePic(imageUrl);
              setGmailConnected(true);
              localStorage.setItem("google_token", token);
              loadInbox("gmail", "inbox");
            }
          }
        }
      } catch (err) {
        console.error("gapi init error:", err);
      }
    };
    gapi.load("client:auth2", initializeGapi);
  }, []);

  // Refetch when folder changes.
  // NOTE: selection is now owned by MailFullClient, which auto-opens the first
  // email once this new list arrives — so there's nothing to clear here.
  useEffect(() => {
    if (gmailConnected) {
      setEmails([]); // clear immediately so the list shows a spinner, not stale items
      loadInbox(activeProvider, activeFolder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder]);

  const loadInbox = async (provider, folder) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (provider === "gmail") {
        const tokenToUse = googleToken || localStorage.getItem("google_token");
        if (!tokenToUse) {
          setLoading(false);
          return;
        }

        response = await api.post("/users/google_login/", {
          mailbox: folder,
          token: tokenToUse,
          clientId: CLIENT_ID,
          clientSecret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
        });
      }
      setEmails(response?.data?.emails || []);
    } catch (err) {
      console.error(`Failed to load ${provider} inbox:`, err);
      setError(`Failed to load ${provider} inbox`);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    setActiveProvider(provider);
    if (provider === "gmail" && !gmailConnected) {
      // TODO: Add Google sign in popup
      return;
    }
    loadInbox(provider, activeFolder);
  };

  // Re-pulls the folder currently on screen. The existing list stays visible
  // while the request is in flight (no blank flash); MailFullClient keeps the
  // open email selected if it's still present after the refresh.
  const handleRefresh = React.useCallback(() => {
    if (!gmailConnected) return;
    loadInbox(activeProvider, activeFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmailConnected, activeProvider, activeFolder, googleToken]);

  // Fetches a single email's full body/attachments and RETURNS it. The full
  // client holds the selected email in its own local state, so this no longer
  // sets parent state (which used to re-render the whole widget on every click).
  const fetchEmailBody = async (emailId) => {
    const tokenToUse = googleToken || localStorage.getItem("google_token");
    if (!tokenToUse) {
      throw new Error("Google authentication token missing");
    }
    const response = await api.get("/users/get-body/", {
      params: { emailId, token: tokenToUse },
    });
    return {
      ...response.data,
      attachments: response.data.attachments || response.data.files || [],
    };
  };

  const handleDisconnect = async (provider) => {
    try {
      if (provider === "gmail") {
        const authInstance = gapi.auth2.getAuthInstance();
        if (authInstance) {
          await authInstance.signOut();
        }
        localStorage.removeItem("google_token");
        setGoogleToken(null);
        setGmailConnected(false);
        setGmailEmail("");
        setGmailProfilePic("");
        setEmails([]);
        toast.success(`Gmail disconnected`);
      }
    } catch (err) {
      console.error(`Failed to disconnect ${provider}:`, err);
      toast.error(`Failed to disconnect ${provider}`);
    }
  };

  return (
    <>
      <MailBoxWidget
        emails={emails}
        loading={loading}
        onOpenInbox={() => setIsInboxOpen(true)}
        gmailConnected={gmailConnected}
        onConnectGmail={async () => {
          try {
            const authInstance = gapi.auth2.getAuthInstance();
            if (!authInstance) {
              toast.error(
                "Google Auth not initialized yet, please try again in a moment."
              );
              return;
            }
            let user = authInstance.currentUser.get();
            if (!user.hasGrantedScopes(SCOPES.join(" "))) {
              user = await authInstance.signIn({
                prompt: "consent",
                scope: SCOPES.join(" "),
              });
            } else if (!authInstance.isSignedIn.get()) {
              user = await authInstance.signIn({ prompt: "consent" });
            }
            const token = user.getAuthResponse(true)?.access_token;
            const email = user.getBasicProfile()?.getEmail() || "";
            const imageUrl = user.getBasicProfile()?.getImageUrl() || "";

            if (token) {
              const clientId = CLIENT_ID;
              const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;

              await api.post("/users/google_login/", {
                token,
                clientId,
                clientSecret,
                provider: "google",
              });

              setGoogleToken(token);
              localStorage.setItem("google_token", token);
              setGmailEmail(email);
              setGmailProfilePic(imageUrl);
              setGmailConnected(true);
              toast.success("Gmail connected successfully!");
              loadInbox("gmail", activeFolder);
            }
          } catch (err) {
            console.error("Failed to connect Gmail via gapi:", err);
            toast.error("Failed to connect Gmail");
          }
        }}
        activeProvider={activeProvider}
        setActiveProvider={handleProviderChange}
        gmailProfilePic={gmailProfilePic}
      />

      <MailFullClient
        open={isInboxOpen}
        onClose={() => setIsInboxOpen(false)}
        emails={emails}
        onFetchEmailBody={fetchEmailBody}
        activeFolder={activeFolder}
        setActiveFolder={setActiveFolder}
        activeProvider={activeProvider}
        setActiveProvider={handleProviderChange}
        gmailConnected={gmailConnected}
        gmailEmail={gmailEmail}
        gmailProfilePic={gmailProfilePic}
        onCompose={() => setIsComposeOpen(true)}
        onLogout={handleDisconnect}
        onRefresh={handleRefresh}
        loading={loading}
        darkMode={darkMode}
      />
    </>
  );
};

export default MailWidget;
